import { useMemo, useContext } from 'react'
import StorageIcon from '@mui/icons-material/Storage'
import CircularProgress from '@mui/material/CircularProgress'
import Fab from '@mui/material/Fab'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsTable, MotionDivContainer, PendingTableRow, pendingStyles } from '@components'
import { Search } from '@components/search'
import { PendingContext, PendingContextProvider } from '@contexts'
import { Toolbar as StyledToolbar } from 'styles/toolbar'
import { usePendingSubmissions } from './hooks/useQueryPending'

const COLUMNS = [
  { label: 'Modified', width: '15%', type: 'date', lookup: 'last_updated' },
  { label: 'Name', width: '20%', lookup: 'name' },
  { label: 'Created By', width: '25%', lookup: 'created_by_id' },
  { label: 'Rows', width: '10%', type: 'number', lookup: 'codes' },
  { label: 'Status', width: '20%', type: 'custom', lookup: 'row_status' },
  { label: 'Actions', width: '10%', type: 'custom', lookup: 'draft' },
]

const getColumnWithDataSource = DATASETS_PAGE_ENABLED => [
  { label: 'Modified', type: 'date', width: '12%', lookup: 'updated_at' },
  { label: 'Name', width: '17%', lookup: 'identifier' },
  { label: DATASETS_PAGE_ENABLED ? 'Dataset' : 'Template', width: '17%', lookup: 'template_name' },
  { label: 'Rows', width: '8%', lookup: 'number_of_rows' },
  { label: 'Source', width: '17%', lookup: 'source' },
  { label: 'Status', width: '15%', lookup: 'type' },
  { label: 'Actions', width: '14%', lookup: 'draft' },
]

export const PendingWithProvider = props => (
  <PendingContextProvider>
    <Pending {...props} />
  </PendingContextProvider>
)

const Pending = ({ match }) => {
  const { state } = useContext(PendingContext)
  const { redirectPath, params: searchParams, handleRowClick, handleSubmissionNameSearch } = state
  const template_id = match.params.id

  const [DATASETS_PAGE_ENABLED, DATA_SOURCE_ENABLED] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE, FeatureDict.SHOW_DATA_SOURCE]),
  )
  const columns = DATA_SOURCE_ENABLED ? getColumnWithDataSource(DATASETS_PAGE_ENABLED) : COLUMNS

  const { isLoading, pendingSubmissions, invalidateDatasetPending } = usePendingSubmissions({
    templateId: template_id,
  })

  const filteredPending = useMemo(
    () =>
      searchParams?.term
        ? pendingSubmissions.filter(pending =>
            pending.name?.toLowerCase().includes(searchParams?.term?.toLowerCase()),
          )
        : pendingSubmissions,
    [pendingSubmissions, searchParams?.term],
  )

  if (redirectPath) {
    return <Redirect push to={redirectPath} />
  }

  if (isLoading) {
    return (
      <MotionDivContainer>
        <pendingStyles.LoadingContainer>
          <CircularProgress color="primary" />
        </pendingStyles.LoadingContainer>
      </MotionDivContainer>
    )
  }

  return (
    <MotionDivContainer>
      <div css={pendingStyles.flexColumn}>
        <pendingStyles.FlexPaper elevation={2} css={pendingStyles.flexColumn}>
          <StyledToolbar>
            <Search onChange={handleSubmissionNameSearch} defaultValue={searchParams?.term} />
          </StyledToolbar>
          <pendingStyles.TableContainer>
            <CvsTable
              headerRowPadding={50}
              columns={columns}
              defaultSort="date"
              keyField="id"
              noAdd
              noPagination
              rows={filteredPending}
              css={[pendingStyles.table, pendingStyles.flexColumn]}
              emptyStateMessage={{
                icon: (
                  <Fab css={pendingStyles.emptyStateFab} disabled>
                    <StorageIcon css={pendingStyles.emptyStateIcon} />
                  </Fab>
                ),
                title: 'There is no pending work for this template.',
              }}
            >
              {({ row, index }) => (
                <PendingTableRow
                  index={index}
                  dataSourceEnabled={DATA_SOURCE_ENABLED}
                  showCreatedByName={row.created_by_name || !DATA_SOURCE_ENABLED}
                  row={{ ...row, template_id }}
                  handleRowClick={handleRowClick}
                  filteredPending={filteredPending}
                  invalidateQueryCache={invalidateDatasetPending}
                  paddingLeftFirstCell={50}
                />
              )}
            </CvsTable>
          </pendingStyles.TableContainer>
        </pendingStyles.FlexPaper>
      </div>
    </MotionDivContainer>
  )
}
